import React from "react";
import {Controller, useFormContext} from "react-hook-form";
import {Box, Chip, Colors, IconAlert, Text} from "@bounce/shared/ui";
import {PaymentFlowFeatureFlag} from "../../../apis/requests/payment-flows/types";
import {MenuItem, Select} from "../../../ui/components";
import FieldLabel from "./FieldLabel";
import FormSection from "./FormSection";
import {BaseFlowFormValues} from "./schema";

const LOAD_ERROR_TEXT = "Failed to load feature flags.";
const NO_FLAGS_TEXT = "No payment test feature flags are defined.";
const SELECT_PLACEHOLDER = "Select a feature flag";
const NO_FLAGS_PLACEHOLDER = "No feature flags available";
const VARIANTS_LABEL = "Variants";
const SELECT_HEIGHT = 40;

type Props = {
  featureFlags: PaymentFlowFeatureFlag[];
  isLoading: boolean;
  isError: boolean;
  noFlagsHint: string;
  onFlagChange: (flag: PaymentFlowFeatureFlag | undefined) => void;
};

const FeatureFlagSection = ({featureFlags, isLoading, isError, noFlagsHint, onFlagChange}: Props) => {
  const {control, setValue, watch} = useFormContext<BaseFlowFormValues>();
  const hasNoFlags = !isLoading && !isError && featureFlags.length === 0;
  const selectedFlag = featureFlags.find((flag) => flag.name === watch("feature_flag"));

  const selectFlag = (name: string) => {
    setValue("feature_flag", name, {shouldDirty: true});
    onFlagChange(featureFlags.find((candidate) => candidate.name === name));
  };

  return (
    <FormSection title="Feature flag">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: selectedFlag ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)",
          columnGap: 2,
          alignItems: "start",
        }}
      >
        <Box>
          <FieldLabel required>Feature flag</FieldLabel>
          <Controller
            name="feature_flag"
            control={control}
            render={({field, fieldState}) => (
              <Select
                {...field}
                fullWidth
                size="small"
                displayEmpty
                disabled={isLoading || isError || hasNoFlags}
                error={!!fieldState.error}
                onChange={(event) => selectFlag(event.target.value as string)}
                renderValue={(selected) =>
                  selected ? (selected as string) : hasNoFlags ? NO_FLAGS_PLACEHOLDER : SELECT_PLACEHOLDER
                }
                sx={{backgroundColor: Colors.bw.white, borderRadius: "8px", fontFamily: "monospace"}}
              >
                {featureFlags.map((flag) => (
                  <MenuItem key={flag.name} value={flag.name} sx={{fontFamily: "monospace"}}>
                    {flag.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {isError && (
            <Text size={12} sx={{color: Colors.red._900, mt: 0.5}}>
              {LOAD_ERROR_TEXT}
            </Text>
          )}
          {hasNoFlags && (
            <Box
              role="alert"
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                mt: 1,
                px: 1.5,
                py: 1.25,
                borderRadius: "8px",
                border: `1px solid ${Colors.orange._400}`,
                backgroundColor: Colors.orange._100,
              }}
            >
              <IconAlert width={18} height={18} color={Colors.orange._900} />
              <Box sx={{display: "flex", flexDirection: "column", gap: 0.25}}>
                <Text size={13} variant="inter600" sx={{color: Colors.orange._900}}>
                  {NO_FLAGS_TEXT}
                </Text>
                <Text size={12} sx={{color: Colors.orange._900}}>
                  {noFlagsHint}
                </Text>
              </Box>
            </Box>
          )}
        </Box>
        {selectedFlag && (
          <Box>
            <FieldLabel>{VARIANTS_LABEL}</FieldLabel>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 0.5,
                minHeight: SELECT_HEIGHT,
                px: 1.5,
                py: 0.75,
                boxSizing: "border-box",
                backgroundColor: Colors.bw.white,
                border: `1px solid ${Colors.tertiary._300}`,
                borderRadius: "8px",
              }}
            >
              {Object.entries(selectedFlag.variant_weights).map(([name, weight]) => (
                <Chip key={name} size_="small" variant_="inherit" label={`${name} · ${weight}%`} />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </FormSection>
  );
};

export default FeatureFlagSection;
